// Filter-Panel der Ticket-Liste (Handoff §1): Status- und Prioritäts-Chips, Kategorie, Zuständig
// (inkl. „Ohne Zuständige“), Objekt, Checklisten-Vorlagen, Zurücksetzen. Werte wie bisher (ein Wert je Filter).
import type { ReactNode } from 'react';
import { ArrowCounterClockwise, FlowArrow, ListChecks } from '@phosphor-icons/react';
import { ImmoButton } from '../../../../components/immobilien/ui';
import { PRIO_REIHENFOLGE, STATUS_REIHENFOLGE } from '../../../../lib/tickets/darstellung';
import { FilterChip, OPTION_STIL, prioFarbe, prioLabel, statusFarbe, statusLabel } from './teile';
import type { ExternOptionen } from '../../../../lib/tickets/externFilter';

export interface FilterPanelProps {
  statusCounts: Record<string, number>;
  filterStatus: string; setFilterStatus: (s: string) => void;
  prioCounts: Record<string, number>;
  filterPrio: string; setFilterPrio: (p: string) => void;
  hauptKategorien: { id: string; name: string }[];
  filterKategorie: string; setFilterKategorie: (k: string) => void;
  teamMembers: { id: string; full_name: string }[];
  teams: { id: string; name: string }[];
  filterZustaendig: string; setFilterZustaendig: (z: string) => void;
  objekte: { id: string; bezeichnung: string }[];
  filterObjekt: string; setFilterObjekt: (o: string) => void;
  externOptionen: ExternOptionen;
  filterExtern: string; setFilterExtern: (e: string) => void;
  onChecklistenVorlagen: () => void;
  onVorgangsVorlagen: () => void;
  hasActiveFilters: boolean;
  resetFilters: () => void;
}

const SELECT_STIL = {
  height: 42, borderRadius: 12, border: 'none', outline: 'none', padding: '0 12px', background: 'var(--fill2)',
  color: 'var(--ink)', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', minWidth: 0, width: '100%', cursor: 'pointer',
} as const;

function Gruppe({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col" style={{ gap: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      {children}
    </div>
  );
}

export function FilterPanel(p: FilterPanelProps) {
  return (
    <section aria-label="Filter" className="immo-glas immo-side-in flex flex-col" style={{ borderRadius: 26, padding: 16, gap: 16 }}>
      <Gruppe label="Status">
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          {STATUS_REIHENFOLGE.map(s => (
            <FilterChip key={s} label={statusLabel(s)} anzahl={p.statusCounts[s] ?? 0} farbe={statusFarbe(s)} aktiv={p.filterStatus === s}
              onClick={() => p.setFilterStatus(p.filterStatus === s ? 'alle' : s)} />
          ))}
        </div>
      </Gruppe>
      <Gruppe label="Priorität">
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          {PRIO_REIHENFOLGE.map(pr => (
            <FilterChip key={pr} label={prioLabel(pr)} anzahl={p.prioCounts[pr] ?? 0} farbe={prioFarbe(pr)} aktiv={p.filterPrio === pr}
              onClick={() => p.setFilterPrio(p.filterPrio === pr ? 'alle' : pr)} />
          ))}
        </div>
      </Gruppe>
      <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <Gruppe label="Kategorie">
          <select aria-label="Kategorie" value={p.filterKategorie} onChange={e => p.setFilterKategorie(e.target.value)} style={SELECT_STIL}
            data-tip="Kategorie – Nur Tickets dieser Kategorie zeigen">
            <option style={OPTION_STIL} value="alle">Alle Kategorien</option>
            {p.hauptKategorien.map(k => <option style={OPTION_STIL} key={k.id} value={k.id}>{k.name}</option>)}
          </select>
        </Gruppe>
        <Gruppe label="Zuständig">
          <select aria-label="Zuständig" value={p.filterZustaendig} onChange={e => p.setFilterZustaendig(e.target.value)} style={SELECT_STIL}
            data-tip="Zuständig – Nach Mitarbeiter, Team oder „ohne Zuständige“ filtern">
            <option style={OPTION_STIL} value="alle">Alle Zuständige</option>
            <option style={OPTION_STIL} value="global">Ohne Zuständige</option>
            <optgroup style={OPTION_STIL} label="Mitarbeiter">
              {p.teamMembers.map(m => <option style={OPTION_STIL} key={m.id} value={m.id}>{m.full_name || 'Unbekannt'}</option>)}
            </optgroup>
            {p.teams.length > 0 && (
              <optgroup style={OPTION_STIL} label="Teams">
                {p.teams.map(t => <option style={OPTION_STIL} key={t.id} value={`t:${t.id}`}>{`Team · ${t.name}`}</option>)}
              </optgroup>
            )}
          </select>
        </Gruppe>
        <Gruppe label="Objekt">
          <select aria-label="Objekt" value={p.filterObjekt} onChange={e => p.setFilterObjekt(e.target.value)} style={SELECT_STIL}
            data-tip="Objekt – Nur Tickets einer Immobilie zeigen">
            <option style={OPTION_STIL} value="alle">Alle Objekte</option>
            {p.objekte.map(o => <option style={OPTION_STIL} key={o.id} value={o.id}>{o.bezeichnung || 'Ohne Bezeichnung'}</option>)}
          </select>
        </Gruppe>
        <Gruppe label="Hausmeister / Dienstleister">
          <select aria-label="Hausmeister / Dienstleister" value={p.filterExtern} onChange={e => p.setFilterExtern(e.target.value)} style={SELECT_STIL}
            data-tip="Hausmeister / Dienstleister – Tickets, die für einen Hausmeister freigegeben oder an einen Dienstleister beauftragt sind">
            <option style={OPTION_STIL} value="alle">Alle Tickets</option>
            <option style={OPTION_STIL} value="extern">Bei Hausmeister oder Dienstleister</option>
            <option style={OPTION_STIL} value="keine">Nur intern (niemand extern)</option>
            <optgroup style={OPTION_STIL} label="Hausmeister">
              <option style={OPTION_STIL} value="hm">Alle Hausmeister-Freigaben</option>
              {p.externOptionen.hausmeister.map(h => <option style={OPTION_STIL} key={h.id} value={`hm:${h.id}`}>{`${h.name} · ${h.anzahl}`}</option>)}
            </optgroup>
            <optgroup style={OPTION_STIL} label="Dienstleister">
              <option style={OPTION_STIL} value="dl">Alle beauftragten Dienstleister</option>
              {p.externOptionen.dienstleister.map(d => <option style={OPTION_STIL} key={d.id} value={`dl:${d.id}`}>{`${d.name} · ${d.anzahl}`}</option>)}
            </optgroup>
          </select>
        </Gruppe>
      </div>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={36} icon={ListChecks} onClick={p.onChecklistenVorlagen} data-tip="Checklisten-Vorlagen – Wiederkehrende Abläufe als Vorlage pflegen">
          Checklisten-Vorlagen
        </ImmoButton>
        <ImmoButton groesse={36} icon={FlowArrow} onClick={p.onVorgangsVorlagen} data-tip="Vorgangs-Vorlagen – Vorgangsarten (Kündigung, Schaden, Zählerstand …) verwalten">
          Vorgangs-Vorlagen
        </ImmoButton>
        {p.hasActiveFilters && (
          <ImmoButton groesse={36} icon={ArrowCounterClockwise} onClick={p.resetFilters} data-tip="Filter zurücksetzen – Alle Filter und die Suche entfernen">
            Filter zurücksetzen
          </ImmoButton>
        )}
      </div>
    </section>
  );
}
